import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { AlertType, Error, Product, PromoKey, ProductCartItem } from '../types/common';
import { RootState } from './store';
import {
    CREATE_PRODUCT_ERROR_MESSAGE,
    DELETE_PRODUCT_ERROR_MESSAGE,
    FETCH_PRODUCTS_ERROR_MESSAGE,
    UPDATE_PRODUCT_ERROR_MESSAGE,
    ARTICLE_DUPLICATE_ERROR_MESSAGE,
} from '../constants/messages';
import { fetchTypesOfFiller } from './FillerTypeSlice';
import { fetchTypesOfFabric } from './FabricTypeSlice';
import { fetchCategories } from './CategorySlice';
import { showAlert } from './CommonSlice';
import { getCsrfToken } from '../utils/helpers';
import { deleteProdGroup } from './ProdGroupSlice';

export type ProductExt = Product & { promoType: PromoKey | null };

export type ProductState = {
    products: Product[];
    selectedProduct: Product;
    isLoading: boolean;
    error: Error;
    missedSlugs: Record<string, true>;
    inFlightSlugs: Record<string, true>;
    redirectTo: string | null;
};

const DEFAULT_PRIORITY = 10;

const initialState: ProductState = {
    products: [],
    selectedProduct: {
        id: '',
        slug: '',
        category: {
            id: '',
            name: '',
            url: '',
        },
        description: '',
        shortDescription: '',
        image: '',
        secondImage: '',
        thirdImage: '',
        fourthImage: '',
        fifthImage: '',
        sixthImage: '',
        name: '',
        price: 0,
        weight: 0,
        grossWeight: 0,
        fillerType: {
            id: '',
            name: '',
        },
        fabricType: {
            id: '',
            name: '',
        },
        quantity: 0,
        rawMaterial: '',
        careInstructions: '',
        brandName: '',
        countryOrigin: '',
        sellerName: '',
        articleNumber: '',
        productСolor: '',
        priority: DEFAULT_PRIORITY,
        discount: null,
        specialDiscount: null,
        dimensions: {
            length: 0,
            width: 0,
            height: 0,
        },
        nameDescription: '',
        complectation: '',
        group: { id: '', name: '', url: '', optionValue: '' },
        season: null,
        promoType: null,
        reviewsEnabled: false,
    },
    isLoading: false,
    error: {
        isError: false,
        message: '',
    },
    missedSlugs: {},
    inFlightSlugs: {},
    redirectTo: null,
};

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

export const fetchProducts = createAsyncThunk<Product[], void, { state: RootState }>('product/fetchProducts', async (_, { getState, dispatch, rejectWithValue }) => {
    await Promise.all([dispatch(fetchTypesOfFiller()), dispatch(fetchTypesOfFabric()), dispatch(fetchCategories())]);
    const response = await fetch(`${BASE_URL}/products`, {
        method: 'GET',
        credentials: 'include',
    });
    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: FETCH_PRODUCTS_ERROR_MESSAGE }));
        return rejectWithValue(FETCH_PRODUCTS_ERROR_MESSAGE);
    }
    const data = await response.json();
    const products: Product[] = data.map((product: any) => ({
        id: product._id,
        slug: product.slug || '',
        ...product,
        fillerType: product.fillerType ?? { id: '', name: '' },
        fabricType: product.fabricType ?? { id: '', name: '' },
        secondImage: product.secondImage || '',
        thirdImage: product.thirdImage || '',
        fourthImage: product.fourthImage || '',
        fifthImage: product.fifthImage || '',
        sixthImage: product.sixthImage || '',
        rawMaterial: product.rawMaterial || '',
        careInstructions: product.careInstructions || '',
        brandName: product.brandName || '',
        countryOrigin: product.countryOrigin || '',
        sellerName: product.sellerName || '',
        articleNumber: product.articleNumber || '',
        productСolor: product.productСolor || '',
        priority: product.priority ?? DEFAULT_PRIORITY,
        discount: product.discount || null,
        specialDiscount: product.specialDiscount || null,
        dimensions: product.dimensions || {
            length: 0,
            width: 0,
            height: 0,
        },
        nameDescription: product.nameDescription || '',
        shortDescription: product.shortDescription || '',
        complectation: product.complectation || '',
        group: product.group || { id: '', name: '', url: '', optionValue: '' },
        season: product.season ?? null,
        promoType: product.promoType ?? null,
        reviewsEnabled: product.reviewsEnabled === true,
    }));
    const {
        fillerType: { typesOfFiller },
        fabricType: { typesOfFabric },
        category: { categories },
    } = getState();
    return products.map((product) => {
        const category = categories.find((cat) => cat.id === product.category?.id);
        const fillerType = typesOfFiller.find((br) => br.id === product.fillerType?.id);
        const fabricType = typesOfFabric.find((br) => br.id === product.fabricType?.id);

        return {
            ...product,
            category: category || product.category,
            fillerType: fillerType || product.fillerType,
            fabricType: fabricType || product.fabricType,
        };
    });
});

// ДЛЯ ЧПУ: получать товар по slug (без загрузки всего каталога)
export const fetchProductBySlug = createAsyncThunk<Product, string, { state: RootState }>(
    'product/fetchProductBySlug',
    async (slug, { rejectWithValue, dispatch, getState }) => {
        if (/^[a-fA-F0-9]{24}$/.test(slug)) {
            return rejectWithValue('Не slug (похоже на идентификатор)');
        }
        const {
            product: { missedSlugs },
        } = getState();
        if (missedSlugs[slug]) {
            return rejectWithValue('Этого slug нет');
        }
        const response = await fetch(`${BASE_URL}/products/slug/${slug}`, { credentials: 'include' });
        if (response.status === 308 || response.status === 301 || response.status === 302) {
            let to = response.headers.get('Location') || '';
            if (!to) {
                try {
                    const j = await response.json();
                    to = j?.redirectTo || '';
                } catch {}
            }
            if (to) {
                return rejectWithValue({ code: 'REDIRECT', to, from: slug });
            }
            return rejectWithValue('Товар не найден');
        }
        if (!response.ok) {
            dispatch(showAlert({ type: AlertType.Error, message: 'Товар не найден' }));
            return rejectWithValue('Товар не найден');
        }
        const data = await response.json();
        const product: Product = {
            id: data._id,
            slug: data.slug || '',
            ...data,
            secondImage: data.secondImage || '',
            thirdImage: data.thirdImage || '',
            fourthImage: data.fourthImage || '',
            fifthImage: data.fifthImage || '',
            sixthImage: data.sixthImage || '',
            rawMaterial: data.rawMaterial || '',
            careInstructions: data.careInstructions || '',
            brandName: data.brandName || '',
            countryOrigin: data.countryOrigin || '',
            sellerName: data.sellerName || '',
            articleNumber: data.articleNumber || '',
            productСolor: data.productСolor || '',
            priority: data.priority ?? DEFAULT_PRIORITY,
            discount: data.discount || null,
            specialDiscount: data.specialDiscount || null,
            dimensions: data.dimensions || { length: 0, width: 0, height: 0 },
            nameDescription: data.nameDescription || '',
            shortDescription: data.shortDescription || '',
            complectation: data.complectation || '',
            group: data.group || { id: '', name: '', url: '', optionValue: '' },
            season: data.season ?? null,
            promoType: data.promoType ?? null,
            reviewsEnabled: data.reviewsEnabled === true,
        };
        return product;
    },
);

// Create product
export const createProduct = createAsyncThunk(
    'product/createProduct',
    async (
        {
            file,
            secondFile,
            thirdFile,
            fourthFile,
            fifthFile,
            sixthFile,
            ...product
        }: Partial<Product & { file: File; secondFile?: File; thirdFile?: File; fourthFile?: File; fifthFile?: File; sixthFile?: File }>,
        { dispatch, rejectWithValue },
    ) => {
        if (!file) {
            const msg = 'Главная иллюстрация обязательна';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(msg);
        }
        const formData = new FormData();
        if (file) formData.append('file', file);
        if (secondFile) formData.append('secondFile', secondFile);
        if (thirdFile) formData.append('thirdFile', thirdFile);
        if (fourthFile) formData.append('fourthFile', fourthFile);
        if (fifthFile) formData.append('fifthFile', fifthFile);
        if (sixthFile) formData.append('sixthFile', sixthFile);
        formData.append('product', JSON.stringify(product));
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${BASE_URL}/products`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'csrf-token': csrfToken,
            },
            body: formData,
        });

        if (!response.ok) {
            if (response.status === 409) {
                dispatch(showAlert({ type: AlertType.Error, message: ARTICLE_DUPLICATE_ERROR_MESSAGE }));
                return rejectWithValue(ARTICLE_DUPLICATE_ERROR_MESSAGE);
            }
            dispatch(showAlert({ type: AlertType.Error, message: CREATE_PRODUCT_ERROR_MESSAGE }));
            return rejectWithValue(CREATE_PRODUCT_ERROR_MESSAGE);
        }

        const data = await response.json();

        dispatch(
            showAlert({
                type: AlertType.Success,
                message: 'Товар успешно создан',
            }),
        );

        return {
            id: data._id || data.id,
            slug: data.slug || '',
            ...product,
            image: data.image,
            secondImage: data.secondImage,
            thirdImage: data.thirdImage,
            fourthImage: data.fourthImage,
            fifthImage: data.fifthImage,
            sixthImage: data.sixthImage,
            priority: product.priority ?? DEFAULT_PRIORITY,
            discount: product.discount ?? null,
            specialDiscount: (product as any).specialDiscount ?? null,
            dimensions: (product as any).dimensions ?? {
                length: 0,
                width: 0,
                height: 0,
            },
            nameDescription: product.nameDescription ?? '',
            complectation: product.complectation ?? '',
            shortDescription: data.shortDescription ?? product.shortDescription ?? '',
            group: (product as any).group ?? { id: '', name: '', url: '', optionValue: '' },
            season: (product as any).season ?? null,
            promoType: (product as any).promoType ?? null,
            reviewsEnabled: data.reviewsEnabled === true,
        } as Product;
    },
);

// Update product
export const updateProduct = createAsyncThunk(
    'product/updateProduct',
    async (
        {
            file,
            secondFile,
            thirdFile,
            fourthFile,
            fifthFile,
            sixthFile,
            ...product
        }: Product & { file?: File; secondFile?: File; thirdFile?: File; fourthFile?: File; fifthFile?: File; sixthFile?: File },
        { dispatch, rejectWithValue },
    ) => {
        const formData = new FormData();
        if (file) formData.append('file', file);
        if (secondFile) formData.append('secondFile', secondFile); // Вторая иллюстрация
        if (thirdFile) formData.append('thirdFile', thirdFile); // Третья иллюстрация
        if (fourthFile) formData.append('fourthFile', fourthFile);
        if (fifthFile) formData.append('fifthFile', fifthFile);
        if (sixthFile) formData.append('sixthFile', sixthFile);
        formData.append('product', JSON.stringify(product));
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${BASE_URL}/products/${product.id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'csrf-token': csrfToken,
            },
            body: formData,
        });

        if (!response.ok) {
            if (response.status === 409) {
                dispatch(showAlert({ type: AlertType.Error, message: ARTICLE_DUPLICATE_ERROR_MESSAGE }));
                return rejectWithValue(ARTICLE_DUPLICATE_ERROR_MESSAGE);
            }
            dispatch(showAlert({ type: AlertType.Error, message: UPDATE_PRODUCT_ERROR_MESSAGE }));
            return rejectWithValue(UPDATE_PRODUCT_ERROR_MESSAGE);
        }

        const data = await response.json();

        dispatch(
            showAlert({
                type: AlertType.Success,
                message: 'Товар успешно обновлён',
            }),
        );

        const resolvedSpecialDiscount =
            data.specialDiscount !== undefined
                ? data.specialDiscount === null && product.specialDiscount
                    ? product.specialDiscount
                    : data.specialDiscount
                : product.specialDiscount;

        return {
            id: product.id,
            slug: data.slug ?? product.slug,
            image: data.image ?? product.image,
            secondImage: data.secondImage !== undefined ? data.secondImage : product.secondImage,
            thirdImage: data.thirdImage !== undefined ? data.thirdImage : product.thirdImage,
            fourthImage: data.fourthImage !== undefined ? data.fourthImage : product.fourthImage,
            fifthImage: data.fifthImage !== undefined ? data.fifthImage : product.fifthImage,
            sixthImage: data.sixthImage !== undefined ? data.sixthImage : product.sixthImage,
            name: data.name ?? product.name,
            description: data.description ?? product.description,
            shortDescription: data.shortDescription ?? product.shortDescription ?? '',
            price: data.price ?? product.price,
            weight: data.weight ?? product.weight,
            grossWeight: data.grossWeight ?? product.grossWeight,
            discount: data.discount ?? product.discount,
            specialDiscount: resolvedSpecialDiscount,
            category: data.category ?? product.category,
            fillerType: data.fillerType ?? product.fillerType,
            fabricType: data.fabricType ?? product.fabricType,
            quantity: data.quantity ?? product.quantity,
            rawMaterial: data.rawMaterial ?? product.rawMaterial,
            careInstructions: data.careInstructions ?? product.careInstructions,
            brandName: data.brandName ?? product.brandName,
            countryOrigin: data.countryOrigin ?? product.countryOrigin,
            sellerName: data.sellerName ?? product.sellerName,
            articleNumber: data.articleNumber ?? product.articleNumber,
            productСolor: data.productСolor ?? product.productСolor,
            nameDescription: data.nameDescription ?? product.nameDescription,
            priority: data.priority ?? product.priority,
            dimensions: data.dimensions ?? product.dimensions,
            complectation: data.complectation ?? product.complectation,
            group: data.group ?? product.group,
            season: data.season ?? product.season,
            promoType: data.promoType ?? product.promoType,
            reviewsEnabled: data.reviewsEnabled === true ? true : false,
        } as Product;
    },
);

// Delete product
export const deleteProduct = createAsyncThunk('product/deleteProduct', async (id: Product['id'], { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/products/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: DELETE_PRODUCT_ERROR_MESSAGE }));
        return rejectWithValue(DELETE_PRODUCT_ERROR_MESSAGE);
    }

    dispatch(
        showAlert({
            type: AlertType.Success,
            message: 'Товар успешно удалён',
        }),
    );

    return id;
});

export const productSlice = createSlice({
    name: 'product',
    initialState,
    reducers: {
        selectProduct: (state, action: PayloadAction<Product['id']>) => {
            const product = state.products.find((product) => product.id === action.payload);

            if (!product) return;

            state.selectedProduct = product;
        },

        setDraftProduct: (state, action: PayloadAction<Product>) => {
            state.selectedProduct = action.payload;
        },

        removeSelectedProduct: (state) => {
            state.selectedProduct = initialState.selectedProduct;
        },

        clearError: (state) => {
            state.error = initialState.error;
        },

        updateAllProductsTypesOfFiller: (state, action: PayloadAction<NonNullable<Product['fillerType']>>) => {
            state.products = state.products.map((product) => {
                if (product.fillerType?.id === action.payload.id) {
                    return { ...product, fillerType: action.payload };
                }
                return product;
            });
        },

        updateAllProductsTypesOfFabric: (state, action: PayloadAction<NonNullable<Product['fabricType']>>) => {
            state.products = state.products.map((product) => {
                if (product.fabricType?.id === action.payload.id) {
                    return { ...product, fabricType: action.payload };
                }
                return product;
            });
        },

        updateAllProductsCategories: (state, action: PayloadAction<Product['category']>) => {
            state.products = state.products.map((product) => {
                if (product.category.id === action.payload.id) {
                    return { ...product, category: action.payload };
                }
                return product;
            });
        },

        updateLocalQuantities: (state, action: PayloadAction<ProductCartItem[]>) => {
            const cartItems = action.payload;
            cartItems.forEach((cartItem) => {
                const productIndex = state.products.findIndex((p) => p.id === cartItem.productId);
                if (productIndex !== -1) {
                    // Вычисляем новое количество, не позволяя уйти в минус
                    const oldQuantity = state.products[productIndex].quantity;
                    const newQuantity = Math.max(oldQuantity - cartItem.quantity, 0);
                    state.products[productIndex].quantity = newQuantity;
                }
            });
        },

        // редьюсер для сброса скидок при логауте
        clearPersonalDiscounts: (state) => {
            state.products = state.products.map((product) => ({
                ...product,
                discount: null,
                specialDiscount: null,
            }));
        },

        clearRedirect: (state) => {
            state.redirectTo = null;
        },
    },

    extraReducers: (builder) => {
        builder.addCase(fetchProducts.pending, (state) => {
            state.isLoading = true;
            state.products = [];
        });

        builder.addCase(fetchProducts.fulfilled, (state, { payload }) => {
            state.products = payload.map((product) => ({
                ...product,
                secondImage: product.secondImage || '',
                thirdImage: product.thirdImage || '',
                fourthImage: product.fourthImage || '',
                fifthImage: product.fifthImage || '',
                sixthImage: product.sixthImage || '',
                discount: product.discount || null,
                specialDiscount: product.specialDiscount || null,
            }));
            state.isLoading = false;
            state.error = initialState.error;
        });

        builder.addCase(fetchProducts.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(fetchProductBySlug.pending, (state, action) => {
            state.isLoading = true;
            state.inFlightSlugs[action.meta.arg] = true;
        });
        builder.addCase(fetchProductBySlug.fulfilled, (state, { payload, meta }) => {
            const idx = state.products.findIndex((p) => p.id === payload.id);
            if (idx >= 0) state.products[idx] = payload;
            else state.products.push(payload);
            state.selectedProduct = payload;
            state.isLoading = false;
            state.error = initialState.error;
            delete state.inFlightSlugs[meta.arg];
            delete state.missedSlugs[meta.arg];
        });
        builder.addCase(fetchProductBySlug.rejected, (state, { payload, meta }) => {
            const p = payload as any;
            if (p && typeof p === 'object' && p.code === 'REDIRECT') {
                state.isLoading = false;
                delete state.inFlightSlugs[meta.arg];
                state.missedSlugs[meta.arg] = true;
                state.redirectTo = p.to.startsWith('/') ? p.to : `/${p.to}`;
                state.error = initialState.error;
                return;
            }
            state.error = {
                isError: true,
                message: (payload as string) || 'Ошибка загрузки товара',
            };
            state.isLoading = false;
            delete state.inFlightSlugs[meta.arg];
            if (payload === 'Товар не найден' || payload === 'Этого slug нет') {
                state.missedSlugs[meta.arg] = true;
            }
        });

        builder.addCase(createProduct.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(createProduct.fulfilled, (state, { payload }) => {
            state.products.push(payload);
            state.isLoading = false;
            state.error = initialState.error;
        });

        builder.addCase(createProduct.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(updateProduct.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(updateProduct.fulfilled, (state, { payload }) => {
            const idx = state.products.findIndex((p) => p.id === payload.id);
            if (idx >= 0) state.products[idx] = payload;
            state.isLoading = false;
            state.error = initialState.error;
            if (state.selectedProduct.id === payload.id) {
                state.selectedProduct = payload;
            }
        });

        builder.addCase(updateProduct.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(deleteProduct.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(deleteProduct.fulfilled, (state, { payload }) => {
            state.products = state.products.filter((p) => p.id !== payload);
            state.isLoading = false;
            state.error = initialState.error;
            if (state.selectedProduct.id === payload) {
                state.selectedProduct = initialState.selectedProduct;
            }
        });

        builder.addCase(deleteProduct.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });
        builder.addCase(deleteProdGroup.fulfilled, (state, { payload }) => {
            state.products = state.products.map((product) =>
                product.group?.id === payload ? { ...product, group: { id: '', name: '', url: '', optionValue: '' } } : product,
            );
            if (state.selectedProduct.group?.id === payload) {
                state.selectedProduct = {
                    ...state.selectedProduct,
                    group: { id: '', name: '', url: '', optionValue: '' },
                };
            }
        });
    },
});

export const {
    selectProduct,
    setDraftProduct,
    removeSelectedProduct,
    clearError,
    updateAllProductsCategories,
    updateLocalQuantities,
    updateAllProductsTypesOfFiller,
    updateAllProductsTypesOfFabric,
    clearPersonalDiscounts,
    clearRedirect,
} = productSlice.actions;

export default productSlice.reducer;
